import React, { SyntheticEvent } from 'react';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import GraphQLClient from '@api/graphql/GraphQLClient';
import * as Queries from '@api/graphql/queries';
import * as Mutations from '@api/graphql/mutations';
import { TimetableTimeSlotsFilters, TimetableTimeSlotStatusType, AdminTimetableSlotsList, AdminTimetableSlotsListVariables, AdminTimetableSlotsList_adminTimetableTimeSlots_data_orders } from '@api/graphql/types';
import Notification from '@components/Notification';
import ReactTable, { RowInfo, SortingRule } from 'react-table';
import DateUtils from '@root/utils/DateUtils';
import { WeightCell } from '@components/Table/Cell';
import { Column } from 'react-virtualized';
import Colors from '@root/utils/Colors';
import { Loading } from '@components/Loading';
import OrdersModal from './OrdersModal';

interface Props {
    sectorId: string;
    filters: TimetableTimeSlotsFilters;
}

type PropType = Props & InjectedIntlProps;


interface State {
    isLoading: boolean;
    data: any;
    paginatorInfo: any;
    modal: {
        orders: AdminTimetableSlotsList_adminTimetableTimeSlots_data_orders[];
        type: 'reserved_order_count' | 'linked_orders';
    };
}

export interface TimeslotRowType {
    id: string;
    linked_orders: number;
    order_limit: number;
    orders: AdminTimetableSlotsList_adminTimetableTimeSlots_data_orders[];
    price: number;
    range_date: string;
    reserved_order_count: number;
    reserved_weight_count: number;
    sector: {
        id: string;
        name: string;
    };
    status: string;
    timeRange: string;
    timeRangeId: string;
    visible_until: string;
    weight_limit: number;
}

export interface SummaryRowType {
    order_limit: number;
    reserved_order_count: number;
    linked_orders: number;
    weight_limit: number;
    reserved_weight_count: number;
}

export interface Summary {
    forenoon: SummaryRowType;
    afternoon: SummaryRowType;
}

export const initial: SummaryRowType = {
    order_limit: 0,
    reserved_order_count: 0,
    linked_orders: 0,
    weight_limit: 0,
    reserved_weight_count: 0,
};

export const increaseSummary = (total: Summary, row: TimeslotRowType) => {
    const forenoonTimeRanges = [1, 2, 3, 4];

    const key = forenoonTimeRanges.includes(parseInt(row.timeRangeId, 10)) ? 'forenoon' : 'afternoon';
    const result = {...total};
    const current = total[key];
    result[key] = {
        order_limit: current.order_limit + row.order_limit,
        reserved_order_count: current.reserved_order_count + row.reserved_order_count,
        linked_orders: current.linked_orders + row.linked_orders,
        weight_limit: current.weight_limit + row.weight_limit,
        reserved_weight_count: current.reserved_weight_count + row.reserved_weight_count,
    };
    return result;
};

export const weightFormat = (data: number) => {
    return `${data / 1000} kg`;
};

class TimetableSlotsTable extends React.Component<PropType, State> {
    private defaultSorted: SortingRule[] = [
        {id: 'timeRange'},
    ];

    private StatusCell = (row : any) => {
        return row.value === TimetableTimeSlotStatusType.FULL ? (<span>Betelt</span>) : (
            <select className="form-control input-sm" value={row.value} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                const variables = {id: row.original.id, status: e.currentTarget.value};
                GraphQLClient.mutate({
                    mutation: Mutations.adminUpdateTimetableTimeSlot, variables,
                }).then(() => {
                    this.fetchData();
                    return Notification.success(this.props.intl.formatMessage({id: 'notification.succeed'}));
                }).catch(() => {
                    Notification.error(this.props.intl.formatMessage({id: 'notification.failed'}));
                });
            }}>
                <option value={TimetableTimeSlotStatusType.ACTIVE}>{this.props.intl.formatMessage({id: 'module.timetable.status.ACTIVE'})}</option>
                <option value={TimetableTimeSlotStatusType.INACTIVE}>{this.props.intl.formatMessage({id: 'module.timetable.status.INACTIVE'})}</option>
            </select>
        );
    }

    private orderClickHandler = (row: any) => (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const data = {
            orders: row.original.orders,
            type: row.column.id,
        };
        this.setState({modal: {...data}});
    }

    private OrderCell = (row : any) => {
        if (!row.value) {
            return <span>{row.value}</span>;
        }

        return (
            <a onClick={this.orderClickHandler(row)}>{row.value}</a>
        );
    }

    private tableColumns = [
        this.getDefaultColumnByName('timeRange'),
        this.getDefaultColumnByName('price'),
        this.getDefaultColumnByName('order_limit'),
        this.getDefaultColumnByName('reserved_order_count', {Cell: this.OrderCell}),
        this.getDefaultColumnByName('linked_orders', {Cell: this.OrderCell}),
        this.getDefaultColumnByName('weight_limit', {Cell: WeightCell}),
        this.getDefaultColumnByName('reserved_weight_count', {Cell: WeightCell}),
        this.getDefaultColumnByName('status', {Cell: this.StatusCell}),
    ];

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.timetable.${id}` });
    }

    private getTdProps = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any) => {
        const style: {color?: string} = {};
        if (rowInfo) {
            const timeSlot = rowInfo.original;
            if (timeSlot.status !== TimetableTimeSlotStatusType.ACTIVE) {
                style.color = Colors.inactive;
            }
            if (timeSlot.reserved_order_count >= timeSlot.order_limit || timeSlot.reserved_weight_count >= timeSlot.weight_limit) {
                return {style, className: 'warning'};
            }
        }
        return {style};
    }

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(props: PropType) {
        if (this.props.filters !== props.filters) {
            this.fetchData();
        }
    }

    private fetchData() {
        this.setState({isLoading: true}, () => {
            const variables: AdminTimetableSlotsListVariables = {
                page: 1,
                count: 1000,
                filters: {
                    id: this.props.filters.id ? this.props.filters.id : undefined,
                    status: this.props.filters.status ? this.props.filters.status : undefined,
                    range_date: this.props.filters.range_date ? DateUtils.formatDate(this.props.filters.range_date) : undefined,
                    timetable_sector_id: this.props.sectorId,
                },
            };
            GraphQLClient.query<AdminTimetableSlotsList>({
                query: Queries.timetableSlots,
                variables,
            }).then(response => {
                const data = response.data.adminTimetableTimeSlots ? response.data.adminTimetableTimeSlots.data.map((item) => {
                    return {...item, timeRangeId: item.timeRange.id, timeRange: `${item.timeRange.time_from}-${item.timeRange.time_to}`, linked_orders: item.orders ?
                        item.orders.filter((order) => (order.parent_order)).length : 0};
                }) : [];
                const paginatorInfo = response.data.adminTimetableTimeSlots ? response.data.adminTimetableTimeSlots.paginatorInfo : {};
                this.setState({data, paginatorInfo, isLoading: false});
            });
        });
    }

    private renderSummary() {
        if (!this.state.data) {
            return '';
        }

        const data: Summary = this.state.data.reduce(increaseSummary, {forenoon: initial, afternoon: initial});

        return (
            <table className="table" style={{marginTop: '30px'}}>
                <thead>
                    <tr>
                        <th />
                        <th>{this.label('order_limit.title')}</th>
                        <th>{this.label('reserved_order_count.title')}</th>
                        <th>{this.label('linked_orders.title')}</th>
                        <th>{this.label('weight_limit.title')}</th>
                        <th>{this.label('reserved_weight_count.title')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{this.label('forenoon')}</td>
                        <td>{data.forenoon.order_limit}</td>
                        <td>{data.forenoon.reserved_order_count}</td>
                        <td>{data.forenoon.linked_orders}</td>
                        <td>{weightFormat(data.forenoon.weight_limit)}</td>
                        <td>{weightFormat(data.forenoon.reserved_weight_count)}</td>
                    </tr>
                    <tr>
                        <td>{this.label('afternoon')}</td>
                        <td>{data.afternoon.order_limit}</td>
                        <td>{data.afternoon.reserved_order_count}</td>
                        <td>{data.afternoon.linked_orders}</td>
                        <td>{weightFormat(data.afternoon.weight_limit)}</td>
                        <td>{weightFormat(data.afternoon.reserved_weight_count)}</td>
                    </tr>
                </tbody>
            </table>
        );
    }

    public render() {
        if (this.state && this.state.isLoading) {
            return <Loading />;
        }

        if (this.state && this.state.data) {
            return (
                <>
                    <ReactTable
                        data={this.state.data}
                        columns={this.tableColumns}
                        defaultPageSize={10}
                        defaultSorted={this.defaultSorted}
                        className="-striped -highlight"
                        getTdProps={this.getTdProps}
                        minRows={1}
                    />
                    {this.renderSummary()}
                    {this.state && this.state.modal && this.state.modal.orders.length && (<OrdersModal
                        type={this.state.modal.type}
                        orders={this.state.modal.orders}
                        onClose={(e?: SyntheticEvent) => {
                            this.setState({modal: {...this.state.modal, orders: []}});
                        }}
                    />)}
                </>
            );
        }
        return '';
    }

}

export default compose(
    injectIntl,
)(TimetableSlotsTable);



// WEBPACK FOOTER //
// ./src/modules/Timetable/components/TimetableSlotsTable.tsx